import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/NotificationsPage.css';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const url = filter === 'unread' 
        ? `/api/notifications?unread=true&page=${page}&limit=20`
        : `/api/notifications?page=${page}&limit=20`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (page === 1) {
        setNotifications(response.data.notifications);
      } else {
        setNotifications(prev => [...prev, ...response.data.notifications]);
      }
      
      setHasMore(response.data.pagination.page < response.data.pagination.pages);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    fetchNotifications();
  }, [filter, page, fetchNotifications]);

  const handleNotificationClick = async (notification) => {
    try {
      const token = localStorage.getItem('token');
      
      // Mark as read
      if (!notification.read) {
        await axios.patch(
          `/api/notifications/${notification._id}/read`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Update local state
        setNotifications(prev => 
          prev.map(n => n._id === notification._id ? { ...n, read: true } : n)
        );
      }

      // Navigate to link if available
      if (notification.link) {
        navigate(notification.link);
      }
    } catch (error) {
      console.error('Error handling notification:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        '/api/notifications/read-all',
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleClearRead = async () => {
    if (!window.confirm('Are you sure you want to delete all read notifications?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete('/api/notifications/clear-read', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.filter(n => !n.read));
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const handleDeleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'account_type_approved': '✅',
      'account_type_rejected': '❌',
      'submission_approved': '✅',
      'submission_rejected': '❌',
      'review_approved': '✅',
      'review_rejected': '❌',
      'report_accepted': '⚠️',
      'report_dismissed': 'ℹ️',
      'giveaway_winner': '🎉',
      'giveaway_entry': '🎮',
      'giveaway_rerolled': '🎉',
      'scammer_request_approved': '✅',
      'scammer_request_rejected': '❌',
      'new_follower': '👤',
      'new_comment': '💬',
      'comment_reply': '💬',
      'warning_received': '⚠️',
      'ban_notice': '🚫',
      'system': 'ℹ️'
    };
    return icons[type] || '🔔';
  };

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    return new Date(date).toLocaleDateString();
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="notifications-page">
      <div className="notifications-container">
        <div className="notifications-page-header">
          <h1>Notifications</h1>
          
          <div className="notifications-actions">
            {unreadCount > 0 && (
              <button className="btn-mark-all-read" onClick={handleMarkAllRead}>
                Mark all as read
              </button>
            )}
            <button className="btn-clear-read" onClick={handleClearRead}>
              Clear read
            </button>
          </div>
        </div>

        <div className="notifications-filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => {
              setFilter('all');
              setPage(1);
            }}
          >
            All
          </button>
          <button
            className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => {
              setFilter('unread');
              setPage(1);
            }}
          >
            Unread {unreadCount > 0 && `(${unreadCount})`}
          </button>
        </div>

        <div className="notifications-list-page">
          {loading && page === 1 ? (
            <div className="notifications-loading">Loading notifications...</div>
          ) : notifications.length === 0 ? (
            <div className="notifications-empty">
              <span className="empty-icon">🔔</span>
              <h3>No notifications yet</h3>
              <p>We'll notify you when something important happens</p>
            </div>
          ) : (
            <>
              {notifications.map(notification => (
                <div
                  key={notification._id}
                  className={`notification-card ${!notification.read ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-card-icon">
                    {getNotificationIcon(notification.type)}
                  </div>
                  
                  <div className="notification-card-content">
                    <div className="notification-card-header">
                      <h3>{notification.title}</h3>
                      {!notification.read && <div className="unread-dot"></div>}
                    </div>
                    <p className="notification-card-message">{notification.message}</p>
                    <div className="notification-card-footer">
                      <span className="notification-time">
                        {formatTimeAgo(notification.createdAt)}
                      </span>
                      {notification.sender && (
                        <span className="notification-sender">
                          from {notification.sender.username}
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    className="notification-delete-btn"
                    onClick={(e) => handleDeleteNotification(notification._id, e)}
                    title="Delete notification"
                  >
                    ×
                  </button>
                </div>
              ))}

              {hasMore && (
                <div className="load-more-container">
                  <button
                    className="btn-load-more"
                    onClick={() => setPage(p => p + 1)}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
